import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  FaUsers, FaClipboardList, FaUserPlus, FaCheckCircle, FaTimesCircle,
  FaLock, FaUnlock, FaKey, FaCopy, FaRedo, FaCalendarAlt, FaUndo,
  FaSearch, FaSort, FaSortUp, FaSortDown, FaDownload, FaSync,
  FaExclamationTriangle, FaClock, FaShieldAlt, FaTimes, FaComment, FaFlask,
} from 'react-icons/fa';
import axios from 'axios';
import CreditManagement from '../dashboard/CreditManagement';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const getExpiryInfo = (expiresAt) => {
  if (!expiresAt) return { label: 'N/A', colorClass: 'bg-gray-100 text-gray-600', daysRemaining: null };
  const now = new Date();
  const exp = new Date(expiresAt);
  const diffMs = exp - now;
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (days < 0) return { label: 'Expired', colorClass: 'bg-red-100 text-red-700', daysRemaining: days };
  if (days <= 7) return { label: `${days}d left`, colorClass: 'bg-yellow-100 text-yellow-700', daysRemaining: days };
  if (days <= 30) return { label: `${days}d left`, colorClass: 'bg-blue-100 text-blue-700', daysRemaining: days };
  return { label: `${days}d left`, colorClass: 'bg-green-100 text-green-700', daysRemaining: days };
};

// ---------------------------------------------------------------------------
// Reusable Modal
// ---------------------------------------------------------------------------
const ConfirmModal = ({ title, message, confirmLabel = 'Confirm', confirmColor = 'bg-brand-red hover:bg-brand-red-dark', inputLabel, inputDefault, onConfirm, onCancel }) => {
  const [inputValue, setInputValue] = useState(inputDefault || '');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <FaTimes />
          </button>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300">{message}</p>
        {inputLabel && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{inputLabel}</label>
            <input
              autoFocus
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-red focus:border-transparent"
              onKeyDown={(e) => { if (e.key === 'Enter') onConfirm(inputValue); }}
            />
          </div>
        )}
        <div className="flex justify-end space-x-3 pt-2">
          <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition">
            Cancel
          </button>
          <button onClick={() => onConfirm(inputLabel ? inputValue : true)} className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition ${confirmColor}`}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Stat Card
// ---------------------------------------------------------------------------
const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 flex items-center space-x-4">
    <div className={`p-3 rounded-lg ${color}`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const AdminDashboard = () => {
  const { token, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('requests');
  const [licenseRequests, setLicenseRequests] = useState([]);
  const [evaluationRequests, setEvaluationRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [generatedPasswords, setGeneratedPasswords] = useState({});
  const [copiedId, setCopiedId] = useState(null);

  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUserData, setNewUserData] = useState({ name: '', email: '', companyName: '', phone: '', position: '' });

  // Modal
  const [modal, setModal] = useState(null);

  // Search / filter / sort (Users tab)
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------
  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const handleApiError = useCallback((err, fallbackMsg) => {
    if (err.response?.status === 401 && err.response?.data?.type === 'expired') { logout(); return; }
    setError(err.response?.data?.error || fallbackMsg);
  }, [logout]);

  const fetchLicenseRequests = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try { setLicenseRequests((await axios.get(`${API_URL}/api/admin/license-requests`, authHeaders)).data.requests || []); }
    catch (err) { handleApiError(err, 'Failed to fetch license requests'); }
    finally { if (showSpinner) setLoading(false); }
  }, [authHeaders, handleApiError]);

  const fetchUsers = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try { setUsers((await axios.get(`${API_URL}/api/admin/users`, authHeaders)).data.users || []); }
    catch (err) { handleApiError(err, 'Failed to fetch users'); }
    finally { if (showSpinner) setLoading(false); }
  }, [authHeaders, handleApiError]);

  const fetchEvaluationRequests = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try { setEvaluationRequests((await axios.get(`${API_URL}/api/admin/evaluation-requests`, authHeaders)).data.requests || []); }
    catch (err) { handleApiError(err, 'Failed to fetch evaluation requests'); }
    finally { if (showSpinner) setLoading(false); }
  }, [authHeaders, handleApiError]);

  const fetchTickets = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try { setTickets((await axios.get(`${API_URL}/api/admin/support-tickets`, authHeaders)).data.tickets || []); }
    catch (err) { handleApiError(err, 'Failed to fetch tickets'); }
    finally { if (showSpinner) setLoading(false); }
  }, [authHeaders, handleApiError]);

  const refreshActiveTab = useCallback(() => {
    if (activeTab === 'requests') fetchLicenseRequests();
    else if (activeTab === 'evaluation') fetchEvaluationRequests();
    else if (activeTab === 'users') fetchUsers();
    else if (activeTab === 'tickets') fetchTickets();
  }, [activeTab, fetchLicenseRequests, fetchEvaluationRequests, fetchUsers, fetchTickets]);

  // Fetch ALL data once on mount so stats cards and tab badges have correct counts
  const [initialLoaded, setInitialLoaded] = useState(false);
  useEffect(() => {
    if (!token || initialLoaded) return;
    setInitialLoaded(true);
    setLoading(true);
    Promise.all([
      fetchLicenseRequests(false),
      fetchEvaluationRequests(false),
      fetchUsers(false),
      fetchTickets(false),
    ]).finally(() => setLoading(false));
  }, [token, initialLoaded, fetchLicenseRequests, fetchEvaluationRequests, fetchUsers, fetchTickets]);

  // Re-fetch the active tab when switching tabs (data may be stale)
  useEffect(() => { if (token && initialLoaded) refreshActiveTab(); }, [activeTab, token, initialLoaded, refreshActiveTab]);

  // ---------------------------------------------------------------------------
  // Flash helpers
  // ---------------------------------------------------------------------------
  const flash = (msg, duration = 3000) => { setSuccessMessage(msg); setTimeout(() => setSuccessMessage(''), duration); };

  // ---------------------------------------------------------------------------
  // Actions (all use modal instead of window.prompt/confirm)
  // ---------------------------------------------------------------------------
  const extendLicense = (licenseId) => {
    setModal({
      title: 'Extend License', message: 'How many days would you like to extend this license?',
      confirmLabel: 'Extend', confirmColor: 'bg-purple-600 hover:bg-purple-700',
      inputLabel: 'Days to extend', inputDefault: '30',
      onConfirm: async (val) => {
        setModal(null);
        const days = parseInt(val);
        if (isNaN(days) || days < 1) { setError('Please enter a positive number (minimum 1)'); return; }
        try {
          const res = await axios.post(`${API_URL}/api/admin/license/${licenseId}/extend`, { days }, authHeaders);
          flash(res.data.message); fetchUsers();
        } catch (err) { handleApiError(err, 'Failed to extend license'); }
      },
    });
  };

  const resetMachineBinding = (licenseId) => {
    setModal({
      title: 'Reset Machine Binding',
      message: 'This will unbind the license from the current machine. The user will need to sign in again on their new machine to re-activate.',
      confirmLabel: 'Reset Machine', confirmColor: 'bg-orange-600 hover:bg-orange-700',
      onConfirm: async () => {
        setModal(null);
        try {
          const res = await axios.post(`${API_URL}/api/admin/license/${licenseId}/reset-machine`, {}, authHeaders);
          flash(res.data.message); fetchUsers();
        } catch (err) { handleApiError(err, 'Failed to reset machine binding'); }
      },
    });
  };

  const downloadLicenseFile = async (licenseId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/license/${licenseId}/download`, { ...authHeaders, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `license_${licenseId}.skm`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (err) { handleApiError(err, 'Failed to download license file'); }
  };

  const approveRequest = async (requestId) => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/license-requests/${requestId}/approve`, {}, authHeaders);
      setGeneratedPasswords(prev => ({ ...prev, [`request_${requestId}`]: res.data.generated_password }));
      flash('User approved! Temporary password generated.', 5000); fetchLicenseRequests();
    } catch (err) { handleApiError(err, 'Failed to approve request'); }
  };

  const rejectRequest = (requestId) => {
    setModal({
      title: 'Reject Request', message: 'Provide a reason for rejection (optional).',
      confirmLabel: 'Reject', confirmColor: 'bg-red-600 hover:bg-red-700',
      inputLabel: 'Reason', inputDefault: '',
      onConfirm: async (reason) => {
        setModal(null);
        try {
          await axios.post(`${API_URL}/api/admin/license-requests/${requestId}/reject`, { reason }, authHeaders);
          flash('Request rejected successfully'); fetchLicenseRequests();
        } catch (err) { handleApiError(err, 'Failed to reject request'); }
      },
    });
  };

  const toggleUserAccess = async (userId) => {
    try {
      const res = await axios.put(`${API_URL}/api/admin/users/${userId}/toggle-access`, {}, authHeaders);
      flash(res.data.message); fetchUsers();
    } catch (err) { handleApiError(err, 'Failed to toggle user access'); }
  };

  const resetPassword = (userId, userName) => {
    setModal({
      title: 'Reset Password',
      message: `Are you sure you want to reset the password for ${userName || 'this user'}?`,
      confirmLabel: 'Reset Password', confirmColor: 'bg-blue-600 hover:bg-blue-700',
      onConfirm: async () => {
        setModal(null);
        try {
          const res = await axios.post(`${API_URL}/api/admin/users/${userId}/reset-password`, {}, authHeaders);
          setGeneratedPasswords(prev => ({ ...prev, [`user_${userId}`]: res.data.new_password }));
          flash('Password reset successfully!', 5000);
        } catch (err) { handleApiError(err, 'Failed to reset password'); }
      },
    });
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_URL}/api/admin/users`, newUserData, authHeaders);
      setGeneratedPasswords(prev => ({ ...prev, [`new_user_${res.data.user_id}`]: res.data.generated_password }));
      flash('User created successfully! Temporary password generated.', 5000);
      setNewUserData({ name: '', email: '', companyName: '', phone: '', position: '' });
      setShowCreateUser(false); fetchUsers();
    } catch (err) { handleApiError(err, 'Failed to create user'); }
  };

  const copyToClipboard = async (text, id) => {
    try { await navigator.clipboard.writeText(text); setCopiedId(id); setTimeout(() => setCopiedId(null), 2000); }
    catch (err) { console.error('Failed to copy:', err); }
  };

  const approveEvaluation = async (requestId) => {
    try {
      await axios.post(`${API_URL}/api/admin/evaluation-requests/${requestId}/approve`, {}, authHeaders);
      flash('Evaluation approved! License file generated.', 5000); fetchEvaluationRequests();
    } catch (err) { handleApiError(err, 'Failed to approve evaluation'); }
  };

  const rejectEvaluation = async (requestId) => {
    try {
      await axios.post(`${API_URL}/api/admin/evaluation-requests/${requestId}/reject`, {}, authHeaders);
      flash('Evaluation request rejected'); fetchEvaluationRequests();
    } catch (err) { handleApiError(err, 'Failed to reject evaluation'); }
  };

  const updateTicketStatus = (ticketId, newStatus) => {
    setModal({
      title: `Update Ticket to "${newStatus.replace('_', ' ')}"`,
      message: 'Add an optional message to the user.',
      confirmLabel: 'Update Status', confirmColor: 'bg-blue-600 hover:bg-blue-700',
      inputLabel: 'Comment (optional)', inputDefault: '',
      onConfirm: async (comment) => {
        setModal(null);
        try {
          await axios.patch(`${API_URL}/api/admin/support-tickets/${ticketId}/status`, { status: newStatus, comment: comment || '' }, authHeaders);
          flash(`Ticket status updated to ${newStatus}`); fetchTickets();
        } catch (err) { handleApiError(err, 'Failed to update ticket status'); }
      },
    });
  };

  const closeTicket = (ticketId) => {
    setModal({
      title: 'Close Ticket', message: 'Are you sure you want to close this ticket?',
      confirmLabel: 'Close Ticket', confirmColor: 'bg-gray-600 hover:bg-gray-700',
      onConfirm: async () => {
        setModal(null);
        try {
          await axios.patch(`${API_URL}/api/admin/support-tickets/${ticketId}/close`, {}, authHeaders);
          flash('Ticket closed successfully'); fetchTickets();
        } catch (err) { handleApiError(err, 'Failed to close ticket'); }
      },
    });
  };

  const addAdminComment = (ticketId) => {
    setModal({
      title: 'Add Comment', message: 'Your comment will be visible to the user.',
      confirmLabel: 'Post Comment', confirmColor: 'bg-blue-600 hover:bg-blue-700',
      inputLabel: 'Comment', inputDefault: '',
      onConfirm: async (comment) => {
        setModal(null);
        if (!comment?.trim()) return;
        try {
          await axios.post(`${API_URL}/api/admin/support-tickets/${ticketId}/comments`, { comment: comment.trim() }, authHeaders);
          flash('Comment added successfully'); fetchTickets();
        } catch (err) { handleApiError(err, 'Failed to add comment'); }
      },
    });
  };

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------
  const pendingRequests = licenseRequests.filter(r => r.status === 'pending');
  const processedRequests = licenseRequests.filter(r => r.status !== 'pending');
  const pendingEvals = evaluationRequests.filter(r => r.status === 'pending');
  const openTickets = tickets.filter(t => t.status !== 'closed' && t.status !== 'resolved');

  // Stats
  const stats = useMemo(() => ({
    totalUsers: users.length,
    activeLicenses: users.filter(u => u.license_status === 'active').length,
    pendingRequests: pendingRequests.length + pendingEvals.length,
    openTickets: openTickets.length,
  }), [users, pendingRequests, pendingEvals, openTickets]);

  // Filtered + sorted users
  const filteredUsers = useMemo(() => {
    let list = users;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(u => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.company_name?.toLowerCase().includes(q));
    }
    if (statusFilter !== 'all') {
      list = list.filter(u => {
        const ld = u.license_details;
        if (statusFilter === 'active') return u.license_status === 'active';
        if (statusFilter === 'inactive') return u.license_status !== 'active';
        if (statusFilter === 'activated') return ld?.license_file_generated && ld?.machine_code;
        if (statusFilter === 'not_activated') return ld && (!ld.license_file_generated || !ld.machine_code);
        if (statusFilter === 'expired') {
          if (!ld?.expires_at) return false;
          return new Date(ld.expires_at) < new Date();
        }
        return true;
      });
    }
    list = [...list].sort((a, b) => {
      let va, vb;
      if (sortField === 'name') { va = a.name?.toLowerCase() || ''; vb = b.name?.toLowerCase() || ''; }
      else if (sortField === 'created_at') { va = a.created_at || ''; vb = b.created_at || ''; }
      else if (sortField === 'license_status') { va = a.license_status || ''; vb = b.license_status || ''; }
      else if (sortField === 'expires_at') { va = a.license_details?.expires_at || ''; vb = b.license_details?.expires_at || ''; }
      else { va = ''; vb = ''; }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [users, searchQuery, statusFilter, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <FaSort className="w-3 h-3 ml-1 opacity-40" />;
    return sortDir === 'asc' ? <FaSortUp className="w-3 h-3 ml-1" /> : <FaSortDown className="w-3 h-3 ml-1" />;
  };

  // ---------------------------------------------------------------------------
  // Tab config
  // ---------------------------------------------------------------------------
  const tabs = [
    { id: 'requests', label: 'Signup Requests', icon: FaClipboardList, badge: pendingRequests.length },
    { id: 'evaluation', label: 'Eval Requests', icon: FaCheckCircle, badge: pendingEvals.length },
    { id: 'users', label: 'Users', icon: FaUsers, badge: users.length },
    { id: 'trial', label: 'Credit Management', icon: FaFlask, badge: 0 },
    { id: 'tickets', label: 'Tickets', icon: FaClipboardList, badge: openTickets.length },
  ];

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Modal */}
      {modal && <ConfirmModal {...modal} onCancel={() => setModal(null)} />}

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Admin Dashboard</h1>
            <p className="mt-1 text-gray-500 dark:text-gray-400">Manage users, licenses, and support</p>
          </div>
          <button onClick={refreshActiveTab} disabled={loading} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition disabled:opacity-50">
            <FaSync className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 flex items-center justify-between bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
            <span className="text-sm">{error}</span>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700"><FaTimes /></button>
          </div>
        )}
        {successMessage && (
          <div className="mb-4 flex items-center justify-between bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg">
            <span className="text-sm">{successMessage}</span>
            <button onClick={() => setSuccessMessage('')} className="text-green-500 hover:text-green-700"><FaTimes /></button>
          </div>
        )}

        {/* Generated Passwords */}
        {Object.keys(generatedPasswords).length > 0 && (
          <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 px-5 py-4 rounded-xl">
            <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-3">Generated Passwords</h3>
            {Object.entries(generatedPasswords).map(([key, password]) => (
              <div key={key} className="mb-2 p-3 bg-white dark:bg-gray-800 rounded-lg border border-blue-200 dark:border-blue-700">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                      {key.startsWith('request_') ? 'Request Approval' : key.startsWith('new_user_') ? 'New User' : 'Password Reset'}
                    </p>
                    <div className="flex items-center space-x-2">
                      <code className="px-3 py-1.5 bg-gray-100 dark:bg-gray-900 text-sm font-mono text-gray-900 dark:text-gray-100 rounded border border-gray-300 dark:border-gray-700">
                        {password}
                      </code>
                      <button onClick={() => copyToClipboard(password, key)} className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg transition" title="Copy">
                        <FaCopy /><span>{copiedId === key ? 'Copied!' : 'Copy'}</span>
                      </button>
                    </div>
                  </div>
                  <button onClick={() => { const p = { ...generatedPasswords }; delete p[key]; setGeneratedPasswords(p); }} className="ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><FaTimes /></button>
                </div>
              </div>
            ))}
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">Copy and send these passwords to users securely. They are cleared on navigation.</p>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard icon={FaUsers} label="Total Users" value={stats.totalUsers} color="bg-blue-600" />
          <StatCard icon={FaShieldAlt} label="Active Licenses" value={stats.activeLicenses} color="bg-green-600" />
          <StatCard icon={FaClock} label="Pending Requests" value={stats.pendingRequests} color="bg-yellow-500" />
          <StatCard icon={FaClipboardList} label="Open Tickets" value={stats.openTickets} color="bg-red-500" />
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="-mb-px flex space-x-6 overflow-x-auto">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`${activeTab === t.id ? 'border-brand-red text-brand-red' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center transition`}>
                <t.icon className="mr-2 w-4 h-4" />
                {t.label}
                {t.badge > 0 && <span className="ml-2 px-1.5 py-0.5 text-xs font-semibold rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">{t.badge}</span>}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300 border-t-brand-red"></div>
          </div>
        ) : (
          <>
            {/* ============ SIGNUP REQUESTS TAB ============ */}
            {activeTab === 'requests' && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Pending Requests</h2>
                {pendingRequests.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-sm py-4">No pending requests</p>
                ) : (
                  <div className="grid gap-4">
                    {pendingRequests.map(req => (
                      <div key={req.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                            <h3 className="text-base font-semibold text-gray-900 dark:text-white">{req.name}</h3>
                            <p><span className="font-medium">Email:</span> {req.email}</p>
                            <p><span className="font-medium">Company:</span> {req.company_name}</p>
                            <p><span className="font-medium">Phone:</span> {req.phone}</p>
                            <p><span className="font-medium">Position:</span> {req.position}</p>
                            <p><span className="font-medium">Requested:</span> {new Date(req.requested_at).toLocaleString()}</p>
                          </div>
                          <div className="flex space-x-2 ml-4">
                            <button onClick={() => approveRequest(req.id)} className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm">
                              <FaCheckCircle className="mr-2" />Approve
                            </button>
                            <button onClick={() => rejectRequest(req.id)} className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm">
                              <FaTimesCircle className="mr-2" />Reject
                            </button>
                          </div>
                        </div>
                        {generatedPasswords[`request_${req.id}`] && (
                          <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded-lg">
                            <p className="text-sm font-semibold text-green-800 dark:text-green-300 mb-2">Temporary Password Generated</p>
                            <div className="flex items-center space-x-2">
                              <code className="flex-1 px-3 py-2 bg-white dark:bg-gray-800 text-sm font-mono rounded border border-green-300 dark:border-green-700">{generatedPasswords[`request_${req.id}`]}</code>
                              <button onClick={() => copyToClipboard(generatedPasswords[`request_${req.id}`], `request_${req.id}`)} className="flex items-center space-x-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition">
                                <FaCopy /><span>{copiedId === `request_${req.id}` ? 'Copied!' : 'Copy'}</span>
                              </button>
                            </div>
                            <p className="text-xs text-green-600 dark:text-green-400 mt-2">Send this password to {req.email}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {processedRequests.length > 0 && (
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Processed Requests</h2>
                    <div className="grid gap-3">
                      {processedRequests.map(req => (
                        <div key={req.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 opacity-70">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-medium text-gray-900 dark:text-white">{req.name}</span>
                              <span className="ml-3 text-sm text-gray-500">{req.email}</span>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${req.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{req.status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ============ EVALUATION TAB ============ */}
            {activeTab === 'evaluation' && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Evaluation & Extension Requests</h2>
                {pendingEvals.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-sm py-4">No pending evaluation requests</p>
                ) : (
                  <div className="grid gap-4">
                    {pendingEvals.map(req => (
                      <div key={req.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                            <h3 className="text-base font-semibold text-gray-900 dark:text-white">{req.users?.name || 'Unknown User'}</h3>
                            <p><span className="font-medium">Email:</span> {req.users?.email}</p>
                            <p><span className="font-medium">Type:</span>{' '}
                              <span className={`px-2 py-0.5 rounded text-xs ${req.type === 'extension' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                                {req.type === 'extension' ? 'Extension' : 'New Evaluation'}
                              </span>
                            </p>
                            <p><span className="font-medium">Requested:</span> {new Date(req.requested_at).toLocaleString()}</p>
                          </div>
                          <div className="flex space-x-2 ml-4">
                            <button onClick={() => approveEvaluation(req.id)} className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm"><FaCheckCircle className="mr-2" />Approve</button>
                            <button onClick={() => rejectEvaluation(req.id)} className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm"><FaTimesCircle className="mr-2" />Reject</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {evaluationRequests.filter(r => r.status !== 'pending').length > 0 && (
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Processed Requests</h2>
                    <div className="grid gap-3">
                      {evaluationRequests.filter(r => r.status !== 'pending').slice(0, 10).map(req => (
                        <div key={req.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 opacity-70">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-medium text-gray-900 dark:text-white">{req.users?.name}</span>
                              <span className="ml-3 text-sm text-gray-500">{req.users?.email}</span>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${req.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{req.status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ============ USERS TAB ============ */}
            {activeTab === 'users' && (
              <div className="space-y-5">
                {/* Toolbar: Search + Filter + Sort + Create */}
                <div className="flex flex-col md:flex-row gap-3">
                  <div className="relative flex-1">
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input type="text" placeholder="Search by name, email, or company..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-brand-red focus:border-transparent" />
                  </div>
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm">
                    <option value="all">All Statuses</option>
                    <option value="active">License Active</option>
                    <option value="inactive">License Inactive</option>
                    <option value="activated">Machine Activated</option>
                    <option value="not_activated">Not Activated</option>
                    <option value="expired">Expired</option>
                  </select>
                  <button onClick={() => setShowCreateUser(!showCreateUser)} className="flex items-center justify-center px-4 py-2.5 bg-brand-red text-white rounded-lg hover:bg-brand-red-dark transition text-sm font-medium">
                    <FaUserPlus className="mr-2" />Create User
                  </button>
                </div>

                {/* Sort bar */}
                <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-medium">Sort by:</span>
                  {[['name', 'Name'], ['created_at', 'Created'], ['license_status', 'License'], ['expires_at', 'Expiry']].map(([f, l]) => (
                    <button key={f} onClick={() => toggleSort(f)} className={`flex items-center px-2 py-1 rounded transition ${sortField === f ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white font-medium' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                      {l}<SortIcon field={f} />
                    </button>
                  ))}
                  <span className="ml-auto text-gray-400">{filteredUsers.length} of {users.length} users</span>
                </div>

                {/* Create User Form */}
                {showCreateUser && (
                  <form onSubmit={createUser} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Create New User</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[['name', 'Name', 'text', true], ['email', 'Email', 'email', true], ['companyName', 'Company Name', 'text', false], ['phone', 'Phone', 'tel', false], ['position', 'Position', 'text', false]].map(([field, ph, type, req]) => (
                        <input key={field} type={type} placeholder={ph} required={req} value={newUserData[field]} onChange={(e) => setNewUserData({ ...newUserData, [field]: e.target.value })}
                          className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-brand-red focus:border-transparent" />
                      ))}
                    </div>
                    <div className="mt-4 flex space-x-2">
                      <button type="submit" className="px-4 py-2 bg-brand-red text-white rounded-lg hover:bg-brand-red-dark transition text-sm">Create User</button>
                      <button type="button" onClick={() => setShowCreateUser(false)} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition text-sm">Cancel</button>
                    </div>
                  </form>
                )}

                {/* Users List */}
                {filteredUsers.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-sm py-4">{searchQuery || statusFilter !== 'all' ? 'No users match your filters' : 'No users found'}</p>
                ) : (
                  <div className="grid gap-4">
                    {filteredUsers.map(user => {
                      const ld = user.license_details;
                      const expiry = ld ? getExpiryInfo(ld.expires_at) : null;
                      const isActivated = ld?.license_file_generated && ld?.machine_code;
                      return (
                        <div key={user.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                          <div className="flex justify-between items-start gap-4">
                            {/* User info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center flex-wrap gap-2 mb-2">
                                <h3 className="text-base font-semibold text-gray-900 dark:text-white">{user.name}</h3>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${user.license_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                  {user.license_status}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${user.account_enabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                  {user.account_enabled ? 'Enabled' : 'Disabled'}
                                </span>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5 text-sm text-gray-600 dark:text-gray-400">
                                <p><span className="font-medium">Email:</span> {user.email}</p>
                                <p><span className="font-medium">Company:</span> {user.company_name || 'N/A'}</p>
                                <p><span className="font-medium">Phone:</span> {user.phone || 'N/A'}</p>
                                <p><span className="font-medium">Position:</span> {user.position || 'N/A'}</p>
                              </div>

                              {/* License Details */}
                              {ld && (
                                <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/40 rounded-lg border border-gray-200 dark:border-gray-600">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center">
                                      <FaCalendarAlt className="w-3 h-3 mr-2 text-brand-purple" />
                                      <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                                        {ld.license_type ? ld.license_type.charAt(0).toUpperCase() + ld.license_type.slice(1) : ''} License
                                      </span>
                                      {ld.count > 1 && <span className="ml-2 text-xs text-gray-400">({ld.count} total)</span>}
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isActivated ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                        {isActivated ? 'Activated' : 'Not Activated'}
                                      </span>
                                      {expiry && (
                                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center ${expiry.colorClass}`}>
                                          {expiry.daysRemaining !== null && expiry.daysRemaining < 0 && <FaExclamationTriangle className="w-3 h-3 mr-1" />}
                                          {expiry.label}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                    <div>
                                      <span className="text-gray-500 dark:text-gray-400">Starts:</span>
                                      <span className="ml-1 font-medium text-gray-900 dark:text-white">{formatDate(ld.start_date)}</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-500 dark:text-gray-400">Expires:</span>
                                      <span className="ml-1 font-medium text-gray-900 dark:text-white">{formatDate(ld.expires_at)}</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-500 dark:text-gray-400">Duration:</span>
                                      <span className="ml-1 font-medium text-gray-900 dark:text-white">{ld.duration_days ? `${ld.duration_days} days` : 'N/A'}</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-500 dark:text-gray-400">Cost:</span>
                                      <span className="ml-1 font-medium text-green-600 dark:text-green-400">{ld.amount ? `$${(ld.amount / 100).toFixed(2)}` : 'N/A'}</span>
                                    </div>
                                  </div>
                                </div>
                              )}
                              {!ld && user.license_status !== 'active' && (
                                <p className="mt-2 text-xs text-gray-400 italic">No licenses assigned</p>
                              )}
                            </div>

                            {/* Action buttons */}
                            <div className="flex flex-col space-y-2 flex-shrink-0">
                              <button onClick={() => toggleUserAccess(user.id)}
                                className={`flex items-center px-3 py-2 rounded-lg transition text-sm font-medium ${user.account_enabled ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-green-600 text-white hover:bg-green-700'}`}>
                                {user.account_enabled ? <><FaLock className="mr-2 w-3.5 h-3.5" />Disable</> : <><FaUnlock className="mr-2 w-3.5 h-3.5" />Enable</>}
                              </button>
                              <button onClick={() => resetPassword(user.id, user.name)} className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium">
                                <FaKey className="mr-2 w-3.5 h-3.5" />Reset Password
                              </button>
                              {ld?.id && (
                                <button onClick={() => extendLicense(ld.id)} className="flex items-center px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-sm font-medium">
                                  <FaCalendarAlt className="mr-2 w-3.5 h-3.5" />Extend License
                                </button>
                              )}
                              {ld?.id && ld?.license_file_generated && (
                                <>
                                  <button onClick={() => resetMachineBinding(ld.id)} className="flex items-center px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-sm font-medium">
                                    <FaUndo className="mr-2 w-3.5 h-3.5" />Reset Machine
                                  </button>
                                  <button onClick={() => downloadLicenseFile(ld.id)} className="flex items-center px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition text-sm font-medium">
                                    <FaDownload className="mr-2 w-3.5 h-3.5" />Download .skm
                                  </button>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Password just reset */}
                          {generatedPasswords[`user_${user.id}`] && (
                            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 rounded-lg">
                              <p className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-2">New Password Generated</p>
                              <div className="flex items-center space-x-2">
                                <code className="flex-1 px-3 py-2 bg-white dark:bg-gray-800 text-sm font-mono rounded border border-blue-300 dark:border-blue-700">{generatedPasswords[`user_${user.id}`]}</code>
                                <button onClick={() => copyToClipboard(generatedPasswords[`user_${user.id}`], `user_${user.id}`)} className="flex items-center space-x-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition">
                                  <FaCopy /><span>{copiedId === `user_${user.id}` ? 'Copied!' : 'Copy'}</span>
                                </button>
                                <button onClick={() => resetPassword(user.id, user.name)} className="flex items-center space-x-1 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg transition" title="Regenerate">
                                  <FaRedo /><span>Regen</span>
                                </button>
                              </div>
                              <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">Send this password to {user.email}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ============ TICKETS TAB ============ */}
            {activeTab === 'tickets' && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Open Tickets ({openTickets.length})</h2>
                {openTickets.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-sm py-4">No open tickets</p>
                ) : (
                  <div className="grid gap-4">
                    {openTickets.map(ticket => (
                      <div key={ticket.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-2">
                              <h3 className="text-base font-semibold text-gray-900 dark:text-white">{ticket.subject}</h3>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                ticket.status === 'open' ? 'bg-blue-100 text-blue-800' :
                                ticket.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                                ticket.status === 'in_review' ? 'bg-purple-100 text-purple-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>{ticket.status.replace('_', ' ').toUpperCase()}</span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                ticket.priority === 'high' ? 'bg-red-100 text-red-800' :
                                ticket.priority === 'normal' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>{ticket.priority?.toUpperCase()}</span>
                            </div>
                            <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                              <p><span className="font-medium">Ticket ID:</span> #{ticket.ticket_id}</p>
                              <p><span className="font-medium">User:</span> {ticket.users?.name} ({ticket.users?.email})</p>
                              <p><span className="font-medium">Category:</span> {ticket.category?.replace('_', ' ')}</p>
                              <p><span className="font-medium">Created:</span> {new Date(ticket.created_at).toLocaleString()}</p>
                              <p className="mt-2 text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{ticket.description}</p>
                              {ticket.attachments?.length > 0 && (
                                <p className="mt-1 text-xs"><span className="font-medium">Attachments:</span> {ticket.attachments.length} file(s)</p>
                              )}
                              {ticket.comments?.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                                  <p className="font-medium text-xs mb-2">Recent Comments:</p>
                                  {ticket.comments.slice(-2).map((c, idx) => (
                                    <div key={idx} className="mb-1 text-xs">
                                      <span className={c.is_admin ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-500'}>{c.is_admin ? 'Support' : 'User'}:</span>{' '}{c.comment}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col space-y-2 flex-shrink-0">
                            <select value={ticket.status} onChange={(e) => updateTicketStatus(ticket.ticket_id, e.target.value)}
                              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                              <option value="open">Open</option>
                              <option value="in_progress">In Progress</option>
                              <option value="in_review">In Review</option>
                              <option value="resolved">Resolved</option>
                            </select>
                            <button onClick={() => addAdminComment(ticket.ticket_id)} className="flex items-center justify-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm">
                              <FaComment className="mr-2 w-3.5 h-3.5" />Add Comment
                            </button>
                            <button onClick={() => closeTicket(ticket.ticket_id)} className="flex items-center justify-center px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition text-sm">
                              <FaTimesCircle className="mr-2 w-3.5 h-3.5" />Close
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Resolved/Closed ({tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length})</h3>
                    <div className="grid gap-3">
                      {tickets.filter(t => t.status === 'resolved' || t.status === 'closed').slice(0, 10).map(ticket => (
                        <div key={ticket.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 opacity-70">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-medium text-gray-900 dark:text-white">{ticket.subject}</span>
                              <span className="ml-3 text-sm text-gray-500">#{ticket.ticket_id} - {ticket.users?.name}</span>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${ticket.status === 'resolved' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{ticket.status?.toUpperCase()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ============ CREDIT MANAGEMENT TAB ============ */}
            {activeTab === 'trial' && (
              <div className="space-y-6">
                <CreditManagement />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
